import classNames from 'classnames';
import { MouseEventHandler } from 'react';

import { Bin, CheckCircle, CheckCircleFilled, InfoCircle } from '@zooplus/icons';
import { Tooltip } from '@zooplus/ui-components';

import MaskedCardNumber from '../MaskedCardNumber/MaskedCardNumber';
import styles from '../StoredCreditCards/StoredCreditCards.module.scss';
import StoredPaymentMethodIcon from '../StoredPaymentMethodIcon/StoredPaymentMethodIcon';

import { useTranslation } from '@hooks/useTranslations';
import { CreditCardData } from '@types-internal';

export type CreditCardProps = {
  creditCard: CreditCardData;
  onDelete?: MouseEventHandler;
  onSelect?: MouseEventHandler;
  showIcons: boolean;
  loadingCardPspIdentifier?: string;
};

/**
 * Display the tile holding all the credit card information and the buttons to interact with it.
 */
const getCardZta = (creditCard: CreditCardData, isSessionCard: boolean) => {
  if (isSessionCard) {
    return 'storedSessionCC';
  }
  if (creditCard.selected) {
    return 'storedSelectedCC';
  }
  return 'storedCC';
};

type CreditCardHeaderProps = {
  creditCard: CreditCardData;
  showIcons: boolean;
  isSessionCard: boolean;
};

const CreditCardHeader = ({ creditCard, showIcons, isSessionCard }: CreditCardHeaderProps) => (
  <div className={styles.creditCardHeader}>
    <div>{<StoredPaymentMethodIcon type={creditCard.brand} />}</div>
    {showIcons && !isSessionCard && (
      <div>
        <span data-zta="checkedCCIcon" className={styles.validity}>
          <SelectionIcon selected={!!creditCard.selected} />
        </span>
      </div>
    )}
  </div>
);

const SelectionIcon = ({ selected }: { selected: boolean }) => {
  if (selected) {
    return <CheckCircleFilled />;
  }
  return <CheckCircle />;
};

type CreditCardBodyProps = {
  creditCard: CreditCardData;
};

const CreditCardBody = ({ creditCard }: CreditCardBodyProps) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className={styles.holderName} data-zta="storedCCHolder">
        {creditCard.holderName ?? '-'}
      </div>
      <div>
        <MaskedCardNumber data-zta="storedCCNumber" lastFourDigits={creditCard.lastFourDigits} />
      </div>
      <div className={styles.expiration}>
        <ExpiryDate data-zta="storedCCDate" creditCard={creditCard} />
        {creditCard.expired && (
          <>
            {' '}
            <span className={styles.expiredLabel}>
              {t('checkout.payment.creditcard.expired.message')}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const CardLoader = ({
  loadingCardPspIdentifier,
  pspIdentifier,
}: {
  loadingCardPspIdentifier?: string;
  pspIdentifier: string;
}) => {
  if (!loadingCardPspIdentifier) {
    return null;
  }
  return (
    <div className={styles.loaderBackdrop}>
      {loadingCardPspIdentifier === pspIdentifier && <div className={styles.loaderLarge} />}
    </div>
  );
};

const CreditCard = ({
  creditCard,
  onDelete,
  onSelect,
  showIcons,
  loadingCardPspIdentifier,
}: CreditCardProps) => {
  const { t } = useTranslation();
  const isSessionCard = creditCard.pspIdentifier === '0';
  const showDelete = showIcons && !!onDelete;

  return (
    <div
      className={styles.creditCardContainer}
      data-card-identifier={creditCard.pspIdentifier}
      data-card-holder-name={creditCard.holderName}
      data-card-last-four-digits={creditCard.lastFourDigits}
      data-card-expiration-month={creditCard.expirationMonth}
      data-card-expiration-year={creditCard.expirationYear}
      data-zta="storedCCWrapper">
      <button
        type="button"
        className={classNames(
          styles.creditCard,
          creditCard.selected && styles.selected,
          creditCard.expired && styles.expired,
        )}
        onClick={onSelect}
        data-zta={getCardZta(creditCard, isSessionCard)}>
        <CreditCardHeader
          creditCard={creditCard}
          showIcons={showIcons}
          isSessionCard={isSessionCard}
        />
        <CreditCardBody creditCard={creditCard} />
      </button>
      {showDelete && (
        <button
          type="button"
          className={styles.delete}
          data-zta="removeCC"
          onClick={onDelete}
          aria-label={t('checkout.payment.creditcard.delete')}>
          <Bin />
        </button>
      )}
      <CardLoader
        loadingCardPspIdentifier={loadingCardPspIdentifier}
        pspIdentifier={creditCard.pspIdentifier}
      />
    </div>
  );
};

type ExpiryDateProps = {
  creditCard: CreditCardData;
  ['data-zta']?: string;
};

/**
 * The component to render the Expiry Date for a credit card.
 */
const ExpiryDate = ({ creditCard, ['data-zta']: dataZta }: ExpiryDateProps) => {
  const { t } = useTranslation();

  const { expirationMonth, expirationYear } = creditCard;

  const isExpiryMonthUnknown = !expirationMonth || expirationMonth === '**';
  const isExpiryYearUnknown = !expirationYear || expirationYear === '**';
  const isExpiryDateUnknown = isExpiryMonthUnknown || isExpiryYearUnknown;

  return (
    <span className={styles.expiryDateContainer} data-zta={dataZta}>
      {isExpiryDateUnknown ? (
        <>
          <span className={styles.creditCardDots}>
            <span className={styles.creditCardDot}>&#11044;</span>
            <span className={styles.creditCardDot}>&#11044;</span>
          </span>
          <span className={styles.separator}>/</span>
          <span className={styles.creditCardDots}>
            <span className={styles.creditCardDot}>&#11044;</span>
            <span className={styles.creditCardDot}>&#11044;</span>
          </span>
        </>
      ) : (
        <>
          <span className={styles.month}>{expirationMonth}</span>
          <span className={styles.separator}>/</span>
          <span className={styles.year}>{expirationYear}</span>
        </>
      )}

      {isExpiryDateUnknown && (
        <Tooltip
          tipId="unknown-expiry-date-tip"
          position="bottom-left"
          tipText={t('checkout.payment.creditcard.expiry_date.unknown')}>
          <InfoCircle aria-label="Unknown expiry date information." />
        </Tooltip>
      )}
    </span>
  );
};

export default CreditCard;
